import React, { useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import LoadingButton from "@material-ui/lab/LoadingButton";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import BoxedLayout from "../../core/components/BoxedLayout";
import { useSnackbar } from "../../core/contexts/SnackbarProvider";
import { useAuth } from "../contexts/AuthProvider";
import { useLocalStorage } from "../../core/hooks/useLocalStorage";

const Login = () => {
  const { isLoggingIn, login, permissions, userInfo } = useAuth();
  const [authKey] = useLocalStorage<string>("authkey", "");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { t } = useTranslation();

  const handleLogin = (email: string, password: string) => {
    login(email, password)
      .then((res) => {
        if (res == false) {
          snackbar.error("Usuário ou senhas incorretos!");
        } else {
          permissions().then(() => {
            snackbar.success("Entrando!");
            navigate(`/admin/dashboard`, {
              replace: true,
            });
          });
        }
      })
      .catch(() => snackbar.error("Usuário ou senhas incorretos!"));
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({}),
    onSubmit: () => handleLogin(formData.email, formData.password),
  });

  // Função para verificar se o token está expirado
  const isTokenExpired = () => {
    const tokenExpire = localStorage.getItem("rh-tokenExpire");
    if (!tokenExpire) return true;

    const expireDate = new Date(tokenExpire);
    const currentDate = new Date();

    return currentDate > expireDate;
  };

  // Função para limpar dados de autenticação
  const clearAuthData = () => {
    localStorage.removeItem("rh-user");
    localStorage.removeItem("rh-userName");
    localStorage.removeItem("rh-userId");
    localStorage.removeItem("rh-email");
    localStorage.removeItem("rh-key");
    localStorage.removeItem("rh-token");
    localStorage.removeItem("rh-refreshToken");
    localStorage.removeItem("rh-tokenExpire");
    localStorage.removeItem("rh-permissions");
    localStorage.removeItem("authkey");
  };

  // Verificar se o usuário já está logado
  useEffect(() => {
    // Se token está expirado, limpar dados
    if (isTokenExpired()) {
      console.warn("Token expirado, limpando dados de autenticação");
      clearAuthData();
      return;
    }

    // Se tem authKey e userInfo válidos, redirecionar para dashboard
    if (authKey && userInfo) {
      console.log("Usuário já autenticado, redirecionando para dashboard");
      navigate("/admin/dashboard", { replace: true });
    }
  }, [authKey, userInfo, navigate]);

  useEffect(() => {
    localStorage.setItem("rh-permissions", JSON.stringify([]));
  }, []);

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: "url(./img/startup.svg)",
          backgroundRepeat: "no-repeat",
          bgcolor: "background.default",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} square>
        <BoxedLayout>
          <Typography component="h1" variant="h5">
            Agnes RH
          </Typography>
          <Box
            component="form"
            marginTop={3}
            noValidate
            onSubmit={formik.handleSubmit}
          >
            <TextField
              margin="normal"
              variant="filled"
              required
              fullWidth
              id="email"
              label="Usuário"
              name="email"
              autoComplete="email"
              autoFocus
              disabled={isLoggingIn}
              value={formData.email}
              onChange={(event) => {
                setFormData({ ...formData, email: event.target.value });
              }}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              margin="normal"
              variant="filled"
              required
              fullWidth
              name="password"
              label="Senha"
              type="password"
              id="password"
              autoComplete="current-password"
              disabled={isLoggingIn}
              value={formData.password}
              onChange={(event) =>
                setFormData({ ...formData, password: event.target.value })
              }
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
            {/*  <Box sx={{ textAlign: "right" }}>
              <Link
                component={RouterLink}
                to={`/forgot-password`}
                variant="body2"
              >
                {t("auth.login.forgotPasswordLink")}
              </Link>
            </Box> */}
            <LoadingButton
              type="submit"
              fullWidth
              loading={isLoggingIn}
              variant="contained"
              sx={{ mt: 3 }}
            >
              Entrar
            </LoadingButton>
            {/* <Button
              component={RouterLink}
              to={`/register`}
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
            >
              {t("auth.login.newAccountLink")}
            </Button> */}
          </Box>
        </BoxedLayout>
      </Grid>
    </Grid>
  );
};

export default Login;
